import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { GoogleLoaderService } from '../loader/google-loader.service';

declare const google: any;

@Injectable({
  providedIn: 'root'
})
export class GooglePlacesService {
  private autocompleteService: google.maps.places.AutocompleteService | null = null;
  private placesService: google.maps.places.PlacesService | null = null;
  private scriptLoaded = false;

  constructor(
    private ngZone: NgZone, 
    private googleLoader: GoogleLoaderService
  ) {}

  private async ensureInitialized(): Promise<void> {
    if (this.scriptLoaded && this.autocompleteService && this.placesService) {
      return;
    }

    await this.googleLoader.load();

    this.autocompleteService = new google.maps.places.AutocompleteService();
    // Create a dummy div for PlacesService as it requires a map or div element
    const dummyDiv = document.createElement('div');
    this.placesService = new google.maps.places.PlacesService(dummyDiv);
    this.scriptLoaded = true;
  }

  getPlacePredictions(input: string): Observable<string[]> {
    return new Observable((observer) => {
      this.ensureInitialized()
      .then(() => {
        if (!input || !this.autocompleteService) {
          observer.next([]);
          observer.complete();
          return;
        }

        const request = {
          input,
          types: ['(cities)'],
          componentRestrictions: { country: 'us' },
        };

        this.autocompleteService.getPlacePredictions(
          request,
          (predictions, status) => this.ngZone.run(() => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              observer.next(predictions.map((p) => p.description));
            } else {
              observer.next([]);
            }
            observer.complete();
          })
        );
      })
      .catch((err) => {
        console.error('Error loading Google Maps script:', err);
        observer.next([]);
        observer.complete();
      });
    });
  }

  getPlaceDetails(placeId: string): Observable<any> {
    return new Observable((observer) => {
      this.ensureInitialized()
        .then(() => {
          if (!placeId || !this.placesService) {
            observer.next(null);
            observer.complete();
            return;
          }

          const request = {
            placeId: placeId,
            fields: ['address_components', 'formatted_address', 'geometry', 'name']
          };

          this.placesService.getDetails(
            request,
            (place, status) => this.ngZone.run(() => {
              if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                observer.next(place);
              } else {
                observer.next(null);
              }
              observer.complete();
            })
          );
        })
        .catch((err) => {
          console.error('Error loading Google Maps script:', err);
          observer.next(null);
          observer.complete();
        });
    });
  }

  getPlacePredictionsWithDetails(input: string): Observable<any[]> {
    return new Observable((observer) => {
      this.ensureInitialized()
      .then(() => {
        if (!input || !this.autocompleteService) {
          observer.next([]);
          observer.complete();
          return;
        }

        const request = {
          input,
          types: ['(cities)'],
          componentRestrictions: { country: 'us' },
        };

        this.autocompleteService.getPlacePredictions(
          request,
          (predictions, status) => this.ngZone.run(() => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              const enrichedPredictions = predictions.map((p) => ({
                description: p.description,
                placeId: p.place_id,
                terms: p.terms
              }));
              observer.next(enrichedPredictions);
            } else {
              observer.next([]);
            }
            observer.complete();
          })
        );
      })
      .catch((err) => {
        console.error('Error loading Google Maps script:', err);
        observer.next([]);
        observer.complete();
      });
    });
  }

  extractAdministrativeAreaLevel2(place: google.maps.places.PlaceResult): string | null {
    if (!place.address_components) return null;
    
    const adminLevel2 = place.address_components.find(
      component => component.types.includes('administrative_area_level_2')
    );
    
    if (!adminLevel2) return null;
    
    // Remove " County" suffix if present (e.g., "Los Angeles County" -> "Los Angeles")
    let name = adminLevel2.long_name;
    if (name.endsWith(' County')) {
      name = name.slice(0, -7); // Remove " County" (7 characters)
    }
    
    return name;
  }

}
